import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  DEFAULT_USER_SETTINGS,
  STUDY_MODES,
  STUDY_MODE_LABELS,
  type StudyMode,
  type UserSettings,
} from '@en3/shared';
import { authApi } from '@/api/auth.api';
import { queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useAuthStatus, useAuthUser, useLogout, useMe } from '@/api/hooks/useAuth';
import { setAccessToken } from '@/api/token';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Badge, Select } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth.store';
import { READER_LIMITS, useReaderSettings } from '@/stores/reader.store';
import { useThemeStore, type ThemeMode } from '@/stores/theme.store';

const ROLE_LABELS: Record<string, string> = {
  student: '学生',
  teacher: '教师',
  admin: '管理员',
};

const THEME_OPTIONS: readonly { value: ThemeMode; label: string }[] = [
  { value: 'light', label: '浅色' },
  { value: 'dark', label: '深色' },
  { value: 'system', label: '跟随系统' },
];

/**
 * 把 epoch 毫秒格式化成中文日期。
 *
 * 非法值（0 / NaN，例如匿名账号没有注册时间）显示破折号，而不是把 "Invalid Date"
 * 摆到用户面前 —— 这类小细节决定页面看起来是否"没做完"。
 */
function formatDate(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '—';
  return new Date(epochMs).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * 个人中心。
 *
 * 登录态由 `authStore` 提供：`loading` 时显示骨架（避免"闪一下未登录"），
 * `anonymous` 时引导登录，`authenticated` 时展示账号信息、学习统计、登录状态，
 * 以及阶段 7 追加的两块：
 *
 * - **偏好设置**：主题 / 默认学习模式 / 阅读器字号行距。
 * - **数据与隐私**：说明平台保存了什么，并提供"停用账号"与"彻底删除数据"两个出口。
 *
 * 结构上的一个刻意安排：两块新增内容都放进 `authenticated` 分支里的子组件。
 * 原因有两个 —— 一是它们各自要发请求（PATCH 设置、DELETE 账号），未登录时不该建立这些
 * 变更操作；二是本组件在登录态分支上有 `return`，如果把新 hook 直接加在下面就会
 * 违反"hook 顺序不随渲染变化"的规则。
 *
 * 另一个细节：注销成功之后要把提示语交给父组件（`onNotice`）。因为那一刻登录态已经
 * 变成 `anonymous`，子组件会被卸载，写在子组件里的成功提示会立刻消失 ——
 * 用户只看到页面整体变成"未登录"，没有任何确认反馈。
 */
export function ProfilePage() {
  const status = useAuthStatus();
  const user = useAuthUser();
  const me = useMe();
  const logout = useLogout();
  const [notice, setNotice] = useState<string | null>(null);

  if (status === 'idle' || status === 'loading') {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <h1 className="text-2xl font-bold">个人中心</h1>
        <p role="status" className="text-sm text-muted-foreground">
          正在恢复登录状态…
        </p>
      </section>
    );
  }

  if (status !== 'authenticated' || !user) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <h1 className="text-2xl font-bold">个人中心</h1>
        {/* 退出登录后状态立刻变为未登录，提示必须在这里也渲染一次，
            否则用户点了"退出登录"只看到页面内容整体换掉、没有任何确认反馈。 */}
        {notice && (
          <p role="status" className="mt-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            {notice}
          </p>
        )}
        <p className="mt-2 text-sm text-muted-foreground">
          你当前是<b>未登录</b>状态。浏览教材与查词无需登录，但生词本、复习记录与水平报告需要登录后才会保存。
        </p>
        <div className="mt-5 flex gap-3">
          <Link
            to={ROUTES.login}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
          >
            去登录
          </Link>
          <Link to={ROUTES.register} className="rounded-md border px-4 py-2 text-sm hover:bg-accent">
            注册账号
          </Link>
        </div>
      </section>
    );
  }

  const stats = me.data?.stats;

  async function handleLogout(allDevices: boolean) {
    setNotice(null);
    await logout.mutateAsync(allDevices);
    setNotice(allDevices ? '已退出全部设备。' : '已退出登录。');
  }

  return (
    <section className="mx-auto max-w-2xl px-5 py-10">
      <h1 className="text-2xl font-bold">个人中心</h1>

      <div className="mt-6 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
        <h2 className="text-sm font-semibold">账号</h2>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">昵称</dt>
            <dd>{user.displayName}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">邮箱</dt>
            <dd className="break-all">{user.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">角色</dt>
            <dd>{ROLE_LABELS[user.role] ?? user.role}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">注册时间</dt>
            <dd>{formatDate(user.createdAt)}</dd>
          </div>
        </dl>
        {user.role === 'admin' && (
          <p className="mt-3 text-xs text-muted-foreground">
            你是管理员，
            <Link to={ROUTES.admin} className="text-primary underline underline-offset-4">
              进入管理后台
            </Link>
            。
          </p>
        )}
      </div>

      <div className="mt-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
        <h2 className="text-sm font-semibold">学习统计</h2>
        {me.isPending ? (
          <p className="mt-2 text-sm text-muted-foreground">加载中…</p>
        ) : me.isError ? (
          <p className="mt-2 text-sm text-destructive">统计加载失败，请稍后重试。</p>
        ) : (
          <dl className="mt-3 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">生词数</dt>
              <dd className="mt-1 text-xl font-semibold">{stats?.wordCount ?? 0}</dd>
            </div>
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">今日待复习</dt>
              <dd className="mt-1 text-xl font-semibold">{stats?.dueToday ?? 0}</dd>
            </div>
            <div className="rounded-md border p-3">
              <dt className="text-xs text-muted-foreground">当前等级</dt>
              <dd className="mt-1 text-xl font-semibold">{stats?.currentCefr ?? '未评估'}</dd>
            </div>
          </dl>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          以上数字随学习行为实时变化；想看得更细（薄弱项、词汇量区间、提升路径）请到
          <Link to={ROUTES.level} className="mx-1 text-primary underline underline-offset-4">
            水平报告
          </Link>
          。
        </p>
      </div>

      <div className="mt-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
        <h2 className="text-sm font-semibold">登录状态</h2>
        <p className="mt-2 text-xs text-muted-foreground">
          访问令牌保存在内存中（最长 15 分钟），刷新页面会用 HttpOnly 刷新凭证自动恢复登录态。
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => void handleLogout(false)} loading={logout.isPending}>
            退出登录
          </Button>
          <Button variant="ghost" onClick={() => void handleLogout(true)} loading={logout.isPending}>
            退出全部设备
          </Button>
        </div>
        {notice && (
          <p role="status" className="mt-3 text-sm text-muted-foreground">
            {notice}
          </p>
        )}
      </div>

      {/* 阶段 7 追加：偏好设置 */}
      <PreferenceSettings user={user} />

      {/* 阶段 7 追加：数据与隐私 */}
      <DataPrivacy onNotice={setNotice} />
    </section>
  );
}

/**
 * 偏好设置（主题 / 默认学习模式 / 阅读器排版）。
 *
 * **本地立即生效 + 登录后同步到账号**，这也是文案里必须讲清楚的一点：
 * 主题与阅读器字号本来就存在 localStorage（`theme.store` / `reader.store`），
 * 它们必须"一改就变"，不能等一个 PATCH 往返；而账号里的设置是为了跨设备一致，
 * 以及给"学习建议"提供默认学习模式。因此每次修改都是**两件事同时发生**：
 * 本地 store 立刻更新（界面马上响应），再异步 PATCH 到 `/api/me/settings`。
 *
 * 请求失败时**不回滚本地设置**：用户在这个设备上的选择依然有效，
 * 只是没同步到账号；把这件事说清楚比"改回去"更符合预期（也避免操作被静默撤销）。
 *
 * 字号/行距用 700ms 防抖后再同步：拖动滑块会产生几十次变化，
 * 每次都发一个 PATCH 既浪费又会因为响应乱序把账号里的值写旧。
 */
function PreferenceSettings({ user }: { user: { settings: UserSettings } }) {
  const queryClient = useQueryClient();

  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);

  const fontSize = useReaderSettings((s) => s.fontSize);
  const lineHeight = useReaderSettings((s) => s.lineHeight);
  const updateReader = useReaderSettings((s) => s.update);

  const [studyMode, setStudyMode] = useState<StudyMode>(
    () => user.settings.defaultStudyMode ?? DEFAULT_USER_SETTINGS.defaultStudyMode,
  );
  const [readerNotice, setReaderNotice] = useState<string | null>(null);

  const syncTimerRef = useRef<number | null>(null);

  const save = useMutation({
    mutationFn: (patch: Partial<UserSettings>) => authApi.updateSettings(patch),
    onSuccess: async (data) => {
      // `authStore.user` 是"当前用户"的唯一真源，设置也必须写回去，
      // 否则同一会话里其它页面读到的还是旧的 user.settings
      useAuthStore.setState((state) =>
        state.user === null ? {} : { user: { ...state.user, settings: data.settings } },
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  // 组件卸载时把还没发出去的同步取消掉，避免对已离开的页面做写入
  useEffect(
    () => () => {
      if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current);
    },
    [],
  );

  function changeTheme(next: ThemeMode) {
    setThemeMode(next); // 立即生效（写 localStorage + 切 <html class="dark">）
    // 上次排版同步留下的提示要清掉，否则"已同步字号 20px"会挂在主题切换之后，误导用户
    setReaderNotice(null);
    save.mutate({ theme: next });
  }

  function changeStudyMode(next: StudyMode) {
    setStudyMode(next);
    setReaderNotice(null);
    save.mutate({ defaultStudyMode: next });
  }

  function syncReader(patch: { fontSize?: number; lineHeight?: number }) {
    updateReader(patch); // 立即生效（写到 CSS 变量，阅读页无需重渲染）
    if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current);
    const nextSize = patch.fontSize ?? fontSize;
    const nextLine = patch.lineHeight ?? lineHeight;
    syncTimerRef.current = window.setTimeout(() => {
      save.mutate({ readerFontSize: nextSize, readerLineHeight: nextLine });
      setReaderNotice(`已同步阅读器排版：字号 ${nextSize}px、行距 ${nextLine}。`);
    }, 700);
  }

  return (
    <div className="mt-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <SlidersHorizontal aria-hidden className="size-4 text-primary" />
        偏好设置
      </h2>
      <p className="mt-2 text-xs text-muted-foreground">
        这些设置<strong>在本设备立即生效</strong>（存在浏览器本地，不需要等网络），
        同时会同步到你的账号，换设备登录后保持一致。
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field id="settings-theme" label="主题" hint="跟随系统会随操作系统的深浅色自动切换。">
          <Select
            id="settings-theme"
            value={themeMode}
            className="w-full"
            onChange={(event) => changeTheme(event.target.value as ThemeMode)}
          >
            {THEME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field id="settings-study-mode" label="默认学习模式" hint="学习建议页会默认选中它。">
          <Select
            id="settings-study-mode"
            value={studyMode}
            className="w-full"
            onChange={(event) => changeStudyMode(event.target.value as StudyMode)}
          >
            {STUDY_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {STUDY_MODE_LABELS[mode]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label htmlFor="settings-font-size" className="flex items-center justify-between gap-2 text-sm">
            <span>阅读器字号</span>
            <span className="text-xs text-muted-foreground tabular-nums">{fontSize} px</span>
          </label>
          <input
            id="settings-font-size"
            type="range"
            min={READER_LIMITS.fontSize.min}
            max={READER_LIMITS.fontSize.max}
            step={READER_LIMITS.fontSize.step}
            value={fontSize}
            className="mt-1 w-full accent-primary"
            onChange={(event) => syncReader({ fontSize: Number(event.target.value) })}
          />
        </div>

        <div>
          <label htmlFor="settings-line-height" className="flex items-center justify-between gap-2 text-sm">
            <span>正文行距</span>
            <span className="text-xs text-muted-foreground tabular-nums">{lineHeight.toFixed(1)}</span>
          </label>
          <input
            id="settings-line-height"
            type="range"
            min={READER_LIMITS.lineHeight.min}
            max={READER_LIMITS.lineHeight.max}
            step={READER_LIMITS.lineHeight.step}
            value={lineHeight}
            className="mt-1 w-full accent-primary"
            onChange={(event) => syncReader({ lineHeight: Number(event.target.value) })}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          排版变化立即作用在阅读页，不需要重新打开文章。想看效果可以
          <Link to={ROUTES.textbooks} className="mx-1 text-primary underline underline-offset-4">
            打开一篇文章
          </Link>
          。
        </p>
      </div>

      {save.isPending && (
        <p role="status" className="mt-3 text-xs text-muted-foreground">
          正在同步到账号…
        </p>
      )}
      {save.isSuccess && !save.isPending && (
        <p role="status" className="mt-3 text-xs text-muted-foreground">
          {readerNotice ?? '已同步到账号。'}
        </p>
      )}
      {save.isError && (
        <p role="alert" className="mt-3 text-xs text-destructive">
          同步到账号失败：{toDisplayMessage(save.error)}。本设备上的设置依然生效。
        </p>
      )}
    </div>
  );
}

/**
 * 数据与隐私。
 *
 * 这一块要先把"平台存了什么"讲清楚，再给两个出口。顺序不能反：
 * 用户要判断"删掉会失去什么"，必须知道有哪些数据。
 *
 * 两个出口的区别（界面上必须说清，否则等于诱导用户做不可逆的操作）：
 * - **停用账号**（`soft`）：撤销全部登录态、账号置为 deleted，数据保留，可由管理员恢复。
 * - **彻底删除**（`purge`）：删除个人数据（生词本、复习日志、阅读进度、评估记录、会话）。
 *   用输入「删除」二字做二次确认 —— 一个"确定吗？"的弹窗对不可逆操作来说太轻了。
 *
 * 删除成功后本地登录态必须立刻清干净。这里**不调用 store 的 `logout()`**：
 * 那个 action 会再打一次 `/api/auth/logout`，而此时会话已被后端撤销，只会拿到 401
 * （虽然它 finally 里也会清状态，但会抛出一个无意义的错误）。直接清本地更准确。
 */
function DataPrivacy({ onNotice }: { onNotice: (message: string) => void }) {
  const queryClient = useQueryClient();
  const [softOpen, setSoftOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const remove = useMutation({
    mutationFn: (mode: 'soft' | 'purge') => authApi.deleteAccount(mode),
    onSuccess: (data, mode) => {
      setSoftOpen(false);
      setPurgeOpen(false);
      setConfirmText('');

      // 清本地登录态：令牌、用户、以及所有已缓存的个人数据
      setAccessToken(null);
      useAuthStore.setState({ user: null, status: 'anonymous' });
      queryClient.clear();

      if (mode === 'purge') {
        onNotice(
          `已彻底删除你的个人数据：生词本记录 ${data.purged?.wordRecords ?? 0} 条，以及复习日志、阅读进度、水平评估记录与全部登录会话（共撤销 ${data.revokedSessions} 个会话）。` +
            '你导入的文章会被保留（其他读者可能还在读），但不再归属于你，也不再出现在「我导入的」列表里。',
        );
      } else {
        onNotice(
          `账号已停用，并撤销了 ${data.revokedSessions} 个登录会话。学习数据（生词本、复习日志、阅读进度、评估记录）都还在，联系管理员可以恢复账号。`,
        );
      }
    },
  });

  const purgeEnabled = confirmText.trim() === '删除';

  return (
    <div className="mt-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <ShieldCheck aria-hidden className="size-4 text-primary" />
        数据与隐私
      </h2>

      <div className="mt-2 space-y-2 text-xs text-muted-foreground">
        <p>你的账号下保存着这些数据（全部用于生成你的学习记录与水平报告，不做其他用途）：</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>生词本：查过/标记过的词、熟悉度、笔记与标签</li>
          <li>复习日志：每次复习的评分与调度结果（用于算记忆保持率）</li>
          <li>阅读进度：每篇文章读到第几句、阅读时长、查词次数</li>
          <li>水平评估记录：每次评估的等级、词汇量区间与特征快照</li>
          <li>登录会话：用于"刷新页面保持登录"的设备凭证</li>
        </ul>
        <p>
          更多说明见
          <Link to={ROUTES.copyright} className="mx-1 text-primary underline underline-offset-4">
            版权与数据来源
          </Link>
          。
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => {
            remove.reset();
            setSoftOpen(true);
          }}
        >
          停用账号
        </Button>
        <Button
          variant="destructive"
          onClick={() => {
            remove.reset();
            setConfirmText('');
            setPurgeOpen(true);
          }}
        >
          <Trash2 aria-hidden className="size-4" />
          彻底删除我的数据
        </Button>
      </div>

      {remove.isError && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          操作失败：{toDisplayMessage(remove.error)}
        </p>
      )}

      {/* 停用：可恢复，确认一次即可 */}
      <Dialog
        open={softOpen}
        onClose={() => setSoftOpen(false)}
        title="停用账号？"
        footer={
          <>
            <Button variant="outline" onClick={() => setSoftOpen(false)}>
              取消
            </Button>
            <Button loading={remove.isPending} onClick={() => remove.mutate('soft')}>
              确认停用
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm">
          <p>停用后会立刻退出所有设备，之后无法登录。</p>
          <ul className="ml-4 list-disc space-y-1 text-xs text-muted-foreground">
            <li>学习数据（生词本、复习日志、阅读进度、评估记录）<strong>全部保留</strong>。</li>
            <li>这是可恢复操作：联系管理员即可重新启用账号，数据不会有损失。</li>
            <li>如果目的是"让个人数据彻底消失"，请改用「彻底删除我的数据」。</li>
          </ul>
        </div>
      </Dialog>

      {/* 彻底删除：不可逆，要求手动输入「删除」二字 */}
      <Dialog
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        title="彻底删除我的数据？"
        footer={
          <>
            <Button variant="outline" onClick={() => setPurgeOpen(false)}>
              取消
            </Button>
            <Button
              variant="destructive"
              disabled={!purgeEnabled}
              loading={remove.isPending}
              onClick={() => remove.mutate('purge')}
            >
              确认彻底删除
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-destructive">这个操作不可撤销，也没有备份可以恢复。</p>
          <ul className="ml-4 list-disc space-y-1 text-xs text-muted-foreground">
            <li><strong>会被删除</strong>：生词本与笔记、复习日志、阅读进度、水平评估记录、全部登录会话。</li>
            <li>
              <strong>会被保留</strong>：你导入的文章本身。其他读者可能还在读它们，所以正文不会跟着消失，
              但会脱离你的归属（不再出现在"我导入的"里，也不再受你的私有可见性约束）。
            </li>
            <li>按平台的合规要求，「版权与数据来源」页会保留必要的导入留痕，用于处理权利人的下架请求。</li>
          </ul>

          <Field id="purge-confirm" label="请输入「删除」以确认">
            <Input
              id="purge-confirm"
              value={confirmText}
              autoComplete="off"
              placeholder="删除"
              onChange={(event) => setConfirmText(event.target.value)}
            />
          </Field>

          <p className="text-xs text-muted-foreground">
            {purgeEnabled ? '已确认，可以执行删除。' : '只有当输入框里正好是「删除」两个字时，删除按钮才会启用。'}
            <Badge tone="muted" className="ml-2">
              当前输入：
              {confirmText.trim().length === 0
                ? '（空）'
                : `${confirmText.trim().slice(0, 12)}${confirmText.trim().length > 12 ? '…' : ''}`}
            </Badge>
          </p>
        </div>
      </Dialog>
    </div>
  );
}
